import type { ApiError } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia
} from '@repo/ui/empty'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
	CirclePause,
	Clock,
	CloudOff,
	Hourglass,
	Link2Off,
	type LucideIcon,
	Timer,
	UserCheck
} from 'lucide-react'
import { useEffect } from 'react'
import {
	changeAppLanguage,
	isAppLanguageCode,
	useTranslation
} from '@/lib/i18n'
import { warmInstanceBranding } from '@/query/query-options'
import { BrandPage, BrandRow } from '@/routes/-components/brand-canvas'
import { ActivationForm } from './-components/activation-form'
import { inviteQueryOptions } from './-query/query-options'

/**
 * The page the link in an invitation email lands on.
 *
 * The URL is not this app's to choose: the backend builds it from its own
 * configured invite path plus the code, and the emails carrying it have
 * already been sent. `/invite/$code` is that path.
 *
 * Public, and `ssr: false`: the person has no session, no password and no
 * second factor, and the flow ends in a `window.location` handoff to their
 * organization's sign-in page. There is nothing here worth a server-rendered
 * first response, and the code should not travel through a server render.
 */
export const Route = createFileRoute('/invite/$code/')({
	ssr: false,
	loader: ({ context }) => warmInstanceBranding(context.queryClient),
	component: InvitePage
})

function InvitePage() {
	const { code } = Route.useParams()
	const { t } = useTranslation()
	const invitation = useQuery(inviteQueryOptions(code))

	const locale = invitation.data?.locale

	useEffect(() => {
		// The invitation was written in one language, and this is the first
		// page its recipient sees. Following it here also decides the language
		// of everything after the sign-in, which is the behaviour the
		// invitation implies.
		if (isAppLanguageCode(locale)) {
			changeAppLanguage(locale)
		}
	}, [locale])

	return (
		<BrandPage>
			{invitation.isLoading && <InvitationLoading />}
			{invitation.isError && (
				<InvitationRefused error={invitation.error} />
			)}
			{invitation.data && (
				<>
					<BrandRow />
					<h1 className='font-semibold text-2xl tracking-tight'>
						{t('client:invite.title', {
							organization: invitation.data.organizationName
						})}
					</h1>
					<p className='mt-2 text-muted-foreground text-sm'>
						{t('client:invite.description')}
					</p>
					<div className='mt-6'>
						<ActivationForm
							code={code}
							invitation={invitation.data}
						/>
					</div>
				</>
			)}
		</BrandPage>
	)
}

function InvitationLoading() {
	const { t } = useTranslation()

	return (
		<output
			aria-label={t('client:invite.loading')}
			className='flex flex-col gap-4'
		>
			<Skeleton className='h-8 w-2/3' />
			<Skeleton className='h-4 w-full' />
			<Skeleton className='mt-2 h-9 w-full' />
		</output>
	)
}

/**
 * Why the link does not work, in the words that tell the reader what to do.
 *
 * Five reasons, five sentences. «Expired» and «already used» point at
 * different next steps — ask to be invited again, or just sign in — and a
 * single «invalid link» would leave both readers stuck. Nobody can reissue
 * their own invitation, so saying which case it is *is* the help.
 */
function InvitationRefused({ error }: { error: unknown }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const code = refusalCodeOf(error)

	const Icon = REFUSAL_ICONS[code]

	// Every refusal reads the same way — an icon, what happened, what to do —
	// and only «already used» has a next step this page can take: the person
	// already has a working account. There is no organization slug to send
	// them straight to its sign-in — the invitation lookup is what failed — so
	// `/login` collecting it again is the honest fallback.
	return (
		<Empty className='p-0'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Icon aria-hidden />
				</EmptyMedia>
				<h1 className='font-semibold text-xl tracking-tight'>
					{t(`client:invite.refused.${code}.title`)}
				</h1>
				<EmptyDescription>
					{t(`client:invite.refused.${code}.description`)}
				</EmptyDescription>
			</EmptyHeader>
			{code === 'already_used' && (
				<EmptyContent>
					<Button onClick={() => navigate({ to: '/login' })}>
						{t('client:invite.refused.already_used.go_to_login')}
					</Button>
				</EmptyContent>
			)}
		</Empty>
	)
}

/**
 * The refusals this page has words for, and the fallback for everything else.
 *
 * A map rather than a chain of comparisons, so adding a refusal to the API is
 * a missing key here rather than a branch nobody wrote. The fallback catches a
 * network failure, which arrives as something that is not an `ApiError` at
 * all, and any code added to the contract after this page was written.
 */
const REFUSAL_KEYS = {
	invite_code_invalid: 'invalid',
	invite_expired: 'expired',
	invite_already_used: 'already_used',
	invite_organization_not_ready: 'organization_not_ready',
	invite_organization_suspended: 'organization_suspended',
	rate_limited: 'rate_limited'
} as const satisfies Partial<Record<ApiError['code'], string>>

const REFUSAL_ICONS = {
	invalid: Link2Off,
	expired: Clock,
	already_used: UserCheck,
	organization_not_ready: Hourglass,
	organization_suspended: CirclePause,
	rate_limited: Timer,
	unavailable: CloudOff
} satisfies Record<
	(typeof REFUSAL_KEYS)[keyof typeof REFUSAL_KEYS] | 'unavailable',
	LucideIcon
>

function refusalCodeOf(error: unknown) {
	const code =
		typeof error === 'object' && error !== null && 'code' in error
			? (error as ApiError).code
			: undefined

	return code && code in REFUSAL_KEYS
		? REFUSAL_KEYS[code as keyof typeof REFUSAL_KEYS]
		: 'unavailable'
}
